// Single source of truth for the "Edit Site" block CMS: every block type
// available on a page, what fields it exposes to the admin editor, and
// which component renders it on the public site.
//
// Field kinds understood by the generic admin editor
// (src/cms/admin/BlockFieldsEditor.jsx):
//   "text"     - single-line string
//   "textarea" - multi-line string
//   "image"    - image URL, edited via upload widget
//   "url"      - link href, plain text input
//   "icon"     - one of src/cms/icons.js's ICON_NAMES
//   "select"   - one of a fixed `options` array (plain strings)
//   "list"     - repeatable array of sub-objects, each shaped by
//                `itemFields` — itemFields can itself include a "list"
//                field, nesting a repeatable list inside each item
//
// Adding a new block type: add an entry here with a fieldsSchema +
// defaultFields + Renderer, then reference its `type` from the relevant
// page's default content file (src/cms/defaultContent/*.js).
//
// `reusable: true` marks a block as available on admin-created custom
// pages (see PAGES vs. the "customPages" Firestore collection, and
// blockTypesForPage below) in addition to whatever fixed `page` it's tied
// to. Custom pages aren't in PAGES at all, so blockTypesForPage() offers
// them every reusable block regardless of `page`.

import ExquraHero from "@/components/home/ExquraHero";
import ExquraPackagesIntro from "@/components/home/ExquraPackages";
import ExquraWhyBook from "@/components/home/ExquraWhyBook";
import ExquraAbout from "@/components/home/ExquraAbout";
import ExquraTestimonials from "@/components/home/ExquraTestimonials";
import ExquraFooter, { withFooterHrefs } from "@/components/home/ExquraFooter";
import ExquraNavbar from "@/components/home/ExquraNavbar";
import MastheadBanner from "@/components/golf/MastheadBanner";
import AboutHero from "@/components/about/AboutHero";
import AboutStats from "@/components/about/AboutStats";
import AboutStory from "@/components/about/AboutStory";
import AboutValues from "@/components/about/AboutValues";
import ContactHero from "@/components/contact/ContactHero";
import ContactDetails from "@/components/contact/ContactDetails";
import CheckoutFootnotes from "@/components/checkout/CheckoutFootnotes";
import GenericRichText from "@/components/cms/GenericRichText";
import GenericFaqList from "@/components/cms/GenericFaqList";
import GenericLinkCards from "@/components/cms/GenericLinkCards";
import PolicyDocument from "@/components/cms/PolicyDocument";

export const PAGES = {
  home: { label: "Home" },
  "about-us": { label: "About Us" },
  "contact-us": { label: "Contact Us" },
  "golf-getaways-masthead": { label: "Getaways & Experience Banner" },
  "about-courses-masthead": { label: "About Courses Banner" },
  footer: { label: "Footer" },
  navbar: { label: "Navigation" },
  checkout: { label: "Review & Pay" },
  // `reusableBlocks`: fixed URL and default content, but freely composed
  // from reusable blocks, the same as a custom page.
  "help-center": { label: "Help Center", reusableBlocks: true },
  faqs: { label: "FAQs", reusableBlocks: true },
  "cancellation-policy": { label: "Cancellation Policy", reusableBlocks: true },
  careers: { label: "Careers", reusableBlocks: true },
  "terms-of-service": { label: "Terms & Conditions", reusableBlocks: true },
  "privacy-policy": { label: "Privacy Policy", reusableBlocks: true },
  // Not block-based — edit-site.jsx special-cases this key to render
  // StringsEditor (src/cms/admin/StringsEditor.jsx) instead of BlockList.
  // Registered here anyway so PagePicker lists it and BUILTIN_PAGE_KEYS
  // correctly excludes it from ever being treated as a custom page.
  "site-text": { label: "Text & Labels" },
};

// Every page key above is a fixed, built-in page. Anything else editable
// through "Edit Site" is an admin-created custom page (registered in the
// "customPages" Firestore collection, not here) — see blockTypesForPage.
const BUILTIN_PAGE_KEYS = new Set(Object.keys(PAGES));

export const BLOCK_REGISTRY = {
  "home-hero": {
    label: "Hero",
    page: "home",
    Renderer: ExquraHero,
    fieldsSchema: [
      {
        key: "slides",
        label: "Slides",
        kind: "list",
        itemLabel: "Slide",
        itemFields: [
          { key: "eyebrow", label: "Eyebrow text", kind: "text" },
          {
            key: "headline",
            label: "Headline",
            kind: "textarea",
            helpText: "Use a line break for a two-line headline.",
          },
          { key: "subtext", label: "Subtext", kind: "text" },
          { key: "backgroundImage", label: "Background image", kind: "image" },
        ],
        helpText:
          "Add more than one slide to turn the hero into a left/right carousel (auto-advances too). One slide behaves exactly like a static hero.",
      },
    ],
    defaultFields: {
      // Slide 1 is untouched — it's the site's actual first impression.
      // Slides 2-4 replicate the "About Courses" page's masthead carousel
      // (masthead-carousel block below), skipping its "Feel the Fairway"
      // slide since that's a duplicate of slide 1 here.
      slides: [
        {
          eyebrow: "EXQURA GOLF & EXPERIENCES",
          headline: "Feel the\nFairway",
          subtext: "Exclusive tee times — book in seconds",
          backgroundImage: "/home.jpeg",
        },
        {
          eyebrow: "EXQURA GOLF & EXPERIENCES",
          headline: "Tee Off in\nParadise",
          subtext: "World-class golf courses at your fingertips",
          backgroundImage:
            "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1400&q=80",
        },
        {
          eyebrow: "EXQURA GOLF & EXPERIENCES",
          headline: "Island Golf\nGetaways",
          subtext: "Ferry, stay & play — all in one booking",
          backgroundImage:
            "https://images.unsplash.com/photo-1611374243147-44a702c2d44c?w=1400&q=80",
        },
        {
          eyebrow: "EXQURA GOLF & EXPERIENCES",
          headline: "Premium\nExperiences",
          subtext: "Spa, coaching & luxury stays included",
          backgroundImage:
            "https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=1400&q=80",
        },
      ],
    },
    // Older published/draft content stored the hero as a flat object
    // (eyebrow/headline/subtext/backgroundImage), from before it became a
    // slides list. Without this, opening the block in "Edit Site" would
    // show an empty Slides list — the old content still renders on the
    // public site (ExquraHero.jsx falls back the same way), but it'd be
    // invisible to the admin editor. Wrapping it as slide 1 here means the
    // very next Save Draft/Publish upgrades the stored shape for good.
    migrateFields: (fields = {}) => {
      if (Array.isArray(fields.slides) && fields.slides.length > 0) {
        return fields;
      }
      return {
        slides: [
          {
            eyebrow: fields.eyebrow ?? "EXQURA GOLF & EXPERIENCES",
            headline: fields.headline ?? "Feel the\nFairway",
            subtext: fields.subtext ?? "Exclusive tee times — book in seconds",
            backgroundImage: fields.backgroundImage ?? "/home.jpeg",
          },
        ],
      };
    },
  },

  "home-packages-intro": {
    label: "Golf Getaways intro",
    page: "home",
    Renderer: ExquraPackagesIntro,
    fieldsSchema: [
      { key: "headline", label: "Headline", kind: "textarea" },
      { key: "subtext", label: "Subtext", kind: "text" },
      { key: "sectionLabel", label: "Section label", kind: "text" },
      { key: "linkLabel", label: "\"View all\" link label", kind: "text" },
    ],
    defaultFields: {
      headline:
        "EXQURA GOLF — YOUR PREMIER CONCIERGE FOR GOLFING EXPERIENCES",
      subtext: "We handle the details, you focus on the game",
      sectionLabel: "Golf Getaways",
      linkLabel: "View all Golf Getaways",
    },
  },

  "home-why-book": {
    label: "Why Book With Exqura",
    page: "home",
    Renderer: ExquraWhyBook,
    fieldsSchema: [
      { key: "headline", label: "Headline", kind: "text" },
      {
        key: "reasons",
        label: "Reasons",
        kind: "list",
        itemLabel: "Reason",
        itemFields: [
          { key: "icon", label: "Icon", kind: "icon" },
          { key: "title", label: "Title", kind: "text" },
          { key: "desc", label: "Description", kind: "text" },
        ],
      },
    ],
    defaultFields: {
      headline: "Why Book With Exqura",
      reasons: [
        {
          icon: "Shield",
          title: "Best Price Guarantee",
          desc: "Unbeatable rates on every booking",
        },
        {
          icon: "Flag",
          title: "Premium Golf Courses",
          desc: "Curated selection of top courses",
        },
        {
          icon: "Zap",
          title: "Instant Confirmation",
          desc: "Book and play without the wait",
        },
        {
          icon: "Headphones",
          title: "Dedicated Concierge",
          desc: "Personal support around the clock",
        },
        {
          icon: "Globe",
          title: "Multi Country Access",
          desc: "Play across 30+ destinations",
        },
        {
          icon: "Lock",
          title: "Secure Booking",
          desc: "Your data and payments protected",
        },
      ],
    },
  },

  "home-about": {
    label: "About Exqura",
    page: "home",
    Renderer: ExquraAbout,
    fieldsSchema: [
      { key: "headline", label: "Headline", kind: "text" },
      { key: "body", label: "Body text", kind: "textarea" },
      { key: "image", label: "Image", kind: "image" },
      { key: "ctaLabel", label: "Link label", kind: "text" },
      { key: "ctaHref", label: "Link URL", kind: "url" },
    ],
    defaultFields: {
      headline: "About Exqura",
      body:
        "At Exqura, we believe that golf is more than a game — it's an experience. We are committed to promoting sustainable golf tourism by partnering with eco-conscious courses and resorts. Every booking you make supports green initiatives and local communities across our destinations, ensuring that the fairways you love today will be there for generations to come.",
      image:
        "https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1920&q=80",
      ctaLabel: "Find Out More",
      ctaHref: "/AboutUs",
    },
  },

  "home-testimonials": {
    label: "Testimonials",
    page: "home",
    reusable: true,
    Renderer: ExquraTestimonials,
    fieldsSchema: [
      { key: "headline", label: "Headline", kind: "text" },
      { key: "subtext", label: "Subtext", kind: "text" },
      {
        key: "testimonials",
        label: "Reviews",
        kind: "list",
        itemLabel: "Review",
        itemFields: [
          { key: "name", label: "Name", kind: "text" },
          { key: "role", label: "Role / location", kind: "text" },
          { key: "quote", label: "Review text", kind: "textarea" },
          {
            key: "rating",
            label: "Rating",
            kind: "select",
            options: ["5", "4", "3", "2", "1"],
          },
          {
            key: "avatar",
            label: "Photo",
            kind: "image",
            helpText: "Leave empty to show the reviewer's initials instead.",
          },
        ],
      },
    ],
    defaultFields: {
      headline: "What Our Golfers Say",
      subtext: "Real experiences from golfers who've traveled with Exqura",
      testimonials: [
        {
          name: "James Whitfield",
          role: "Golfer from Singapore",
          quote:
            "Exqura made booking our golf trip to Bali completely effortless. Every tee time, transfer, and hotel was arranged perfectly — we just showed up and played.",
          rating: "5",
          avatar: "",
        },
        {
          name: "Priya Nair",
          role: "Golfer from Kuala Lumpur",
          quote:
            "The concierge team went above and beyond, rearranging our tee times at the last minute without any hassle. Best golf travel experience we've had.",
          rating: "5",
          avatar: "",
        },
        {
          name: "David Chen",
          role: "Golfer from Hong Kong",
          quote:
            "Instant confirmation, great course selection, and unbeatable prices. Exqura has become our go-to for every golf getaway in Asia.",
          rating: "5",
          avatar: "",
        },
        {
          name: "Sarah Thompson",
          role: "Golfer from Sydney",
          quote:
            "From the ferry transfer to the resort to the fairway, everything was seamless. It genuinely felt like a five-star concierge service.",
          rating: "4",
          avatar: "",
        },
        {
          name: "Arjun Mehta",
          role: "Golfer from Mumbai",
          quote:
            "We booked a group trip for eight golfers and Exqura handled every detail. Communication was fast and the courses were exceptional.",
          rating: "5",
          avatar: "",
        },
        {
          name: "Emily Carter",
          role: "Golfer from London",
          quote:
            "Booking across time zones used to be a nightmare. Exqura's platform made planning our Southeast Asia golf tour simple and stress-free.",
          rating: "5",
          avatar: "",
        },
      ],
    },
  },

  "about-hero": {
    label: "Hero",
    page: "about-us",
    reusable: true,
    Renderer: AboutHero,
    fieldsSchema: [
      { key: "eyebrow", label: "Eyebrow text", kind: "text" },
      {
        key: "headline",
        label: "Headline",
        kind: "text",
        helpText: "The last word is highlighted in gold automatically.",
      },
      { key: "subtext", label: "Subtext", kind: "textarea" },
      { key: "backgroundImage", label: "Background image", kind: "image" },
    ],
    defaultFields: {
      eyebrow: "Who We Are",
      headline: "About Exqura",
      subtext:
        "We are your premier concierge for golfing experiences — curating unforgettable rounds at the world's most prestigious courses with seamless end-to-end service.",
      backgroundImage:
        "https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1920&q=80",
    },
  },

  "about-stats": {
    label: "Stats",
    page: "about-us",
    reusable: true,
    Renderer: AboutStats,
    fieldsSchema: [
      {
        key: "stats",
        label: "Stats",
        kind: "list",
        itemLabel: "Stat",
        itemFields: [
          { key: "value", label: "Value", kind: "text" },
          { key: "label", label: "Label", kind: "text" },
        ],
      },
    ],
    defaultFields: {
      stats: [
        { value: "120+", label: "Golf Courses" },
        { value: "30+", label: "Destinations" },
        { value: "15K+", label: "Happy Golfers" },
        { value: "8", label: "Years of Excellence" },
      ],
    },
  },

  "about-story": {
    label: "Our Story",
    page: "about-us",
    reusable: true,
    Renderer: AboutStory,
    fieldsSchema: [
      { key: "eyebrow", label: "Eyebrow text", kind: "text" },
      { key: "headline", label: "Headline", kind: "text" },
      { key: "paragraph1", label: "Paragraph 1", kind: "textarea" },
      { key: "paragraph2", label: "Paragraph 2", kind: "textarea" },
      { key: "ctaLabel", label: "Link label", kind: "text" },
      { key: "ctaHref", label: "Link URL", kind: "url" },
      { key: "image", label: "Image", kind: "image" },
    ],
    defaultFields: {
      eyebrow: "Our Story",
      headline: "Born from a passion for the perfect round",
      paragraph1:
        "Exqura was founded by a group of avid golfers who were frustrated by the complexity of planning international golf trips. Booking tee times across time zones, coordinating transfers, finding the right hotel — it was all too fragmented.",
      paragraph2:
        "We built Exqura to change that. Today, we offer a single platform where golfers can discover, book, and experience the finest courses across Asia — all in seconds, all in one place.",
      ctaLabel: "Read our full story",
      ctaHref: "#",
      image:
        "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1200&q=80",
    },
  },

  "about-values": {
    label: "Core Values",
    page: "about-us",
    reusable: true,
    Renderer: AboutValues,
    fieldsSchema: [
      { key: "eyebrow", label: "Eyebrow text", kind: "text" },
      { key: "headline", label: "Headline", kind: "text" },
      {
        key: "values",
        label: "Values",
        kind: "list",
        itemLabel: "Value",
        itemFields: [
          { key: "icon", label: "Icon", kind: "icon" },
          { key: "title", label: "Title", kind: "text" },
          { key: "desc", label: "Description", kind: "text" },
        ],
      },
    ],
    defaultFields: {
      eyebrow: "What We Stand For",
      headline: "Our Core Values",
      values: [
        {
          icon: "Trophy",
          title: "Premium Quality",
          desc: "We partner only with the world's finest courses and resorts, ensuring every round exceeds expectations.",
        },
        {
          icon: "Leaf",
          title: "Sustainable Tourism",
          desc: "Every booking supports eco-conscious courses and local communities, preserving the fairways for generations.",
        },
        {
          icon: "Globe",
          title: "Global Reach",
          desc: "From Batam to Bali, Bangkok to Hua Hin — we unlock exclusive tee times across Asia and beyond.",
        },
        {
          icon: "Users",
          title: "Dedicated Concierge",
          desc: "Our team handles every detail — flights, hotels, transfers, and tee times — so you can focus on your game.",
        },
      ],
    },
  },

  "contact-hero": {
    label: "Hero",
    page: "contact-us",
    Renderer: ContactHero,
    fieldsSchema: [
      { key: "eyebrow", label: "Eyebrow text", kind: "text" },
      {
        key: "headline",
        label: "Headline",
        kind: "text",
        helpText: "The last word is highlighted in gold automatically.",
      },
      { key: "subtext", label: "Subtext", kind: "textarea" },
    ],
    defaultFields: {
      eyebrow: "Get In Touch",
      headline: "Contact Us",
      subtext:
        "Have a question, need help with a booking, or want to plan a bespoke golf trip? We'd love to hear from you.",
    },
  },

  "contact-details": {
    label: "Contact Details",
    page: "contact-us",
    reusable: true,
    Renderer: ContactDetails,
    fieldsSchema: [
      {
        key: "items",
        label: "Contact methods",
        kind: "list",
        itemLabel: "Contact method",
        itemFields: [
          { key: "icon", label: "Icon", kind: "icon" },
          { key: "label", label: "Label", kind: "text" },
          { key: "value", label: "Value", kind: "text" },
          { key: "sub", label: "Sub-text", kind: "text" },
        ],
      },
      { key: "detailsHeading", label: "“Reach Out Directly” heading", kind: "text" },
      { key: "formHeading", label: "Form heading", kind: "text" },
      { key: "nameLabel", label: "Name label", kind: "text" },
      { key: "namePlaceholder", label: "Name placeholder", kind: "text" },
      { key: "emailLabel", label: "Email label", kind: "text" },
      { key: "emailPlaceholder", label: "Email placeholder", kind: "text" },
      { key: "subjectLabel", label: "Subject label", kind: "text" },
      { key: "subjectPlaceholder", label: "Subject placeholder", kind: "text" },
      { key: "messageLabel", label: "Message label", kind: "text" },
      { key: "messagePlaceholder", label: "Message placeholder", kind: "text" },
      { key: "submitLabel", label: "Send button", kind: "text" },
      { key: "successTitle", label: "Success heading", kind: "text" },
      { key: "successText", label: "Success message", kind: "textarea" },
      { key: "resetLabel", label: "“Send another” link", kind: "text" },
    ],
    defaultFields: {
      items: [
        { icon: "Mail", label: "Email Us", value: "hello@exqura.com", sub: "We reply within 24 hours" },
        { icon: "Phone", label: "Call Us", value: "+65 6123 4567", sub: "Mon – Fri, 9am – 6pm SGT" },
        { icon: "MapPin", label: "Our Office", value: "Singapore", sub: "1 Raffles Place, #20-61" },
        { icon: "Clock", label: "Support Hours", value: "24 / 7", sub: "For urgent booking assistance" },
      ],
      detailsHeading: "Reach Out Directly",
      formHeading: "Send Us a Message",
      nameLabel: "Name",
      namePlaceholder: "Your name",
      emailLabel: "Email",
      emailPlaceholder: "your@email.com",
      subjectLabel: "Subject",
      subjectPlaceholder: "How can we help?",
      messageLabel: "Message",
      messagePlaceholder: "Tell us more...",
      submitLabel: "Send Message",
      successTitle: "Message Sent!",
      successText: "Thank you for reaching out. Our team will get back to you within 24 hours.",
      resetLabel: "Send another message",
    },
  },

  "masthead-carousel": {
    label: "Hero Carousel",
    page: "golf-getaways-masthead",
    Renderer: MastheadBanner,
    fieldsSchema: [
      {
        key: "slides",
        label: "Slides",
        kind: "list",
        itemLabel: "Slide",
        itemFields: [
          { key: "image", label: "Image", kind: "image" },
          { key: "headline", label: "Headline", kind: "text" },
          { key: "sub", label: "Subtext", kind: "text" },
        ],
      },
    ],
    defaultFields: {
      slides: [
        {
          image: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1400&q=80",
          headline: "Tee Off in Paradise",
          sub: "World-class golf courses at your fingertips",
        },
        {
          image: "https://images.unsplash.com/photo-1500932334442-8761ee4810a7?w=1400&q=80",
          headline: "Feel the Fairway",
          sub: "Exclusive tee times — book in seconds",
        },
        {
          image: "https://images.unsplash.com/photo-1611374243147-44a702c2d44c?w=1400&q=80",
          headline: "Island Golf Getaways",
          sub: "Ferry, stay & play — all in one booking",
        },
        {
          image: "https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=1400&q=80",
          headline: "Premium Experiences",
          sub: "Spa, coaching & luxury stays included",
        },
      ],
    },
  },

  "footer-content": {
    label: "Footer",
    page: "footer",
    Renderer: ExquraFooter,
    // Footers published before these pages existed still store "#" hrefs.
    migrateFields: (fields = {}) => ({
      ...fields,
      supportLinks: withFooterHrefs(fields.supportLinks),
      companyLinks: withFooterHrefs(fields.companyLinks),
    }),
    fieldsSchema: [
      { key: "brandBlurb", label: "Brand blurb", kind: "textarea" },
      {
        key: "supportLinks",
        label: "Support links",
        kind: "list",
        itemLabel: "Link",
        itemFields: [
          { key: "label", label: "Label", kind: "text" },
          { key: "href", label: "URL", kind: "url" },
        ],
      },
      {
        key: "companyLinks",
        label: "Company links",
        kind: "list",
        itemLabel: "Link",
        itemFields: [
          { key: "label", label: "Label", kind: "text" },
          { key: "href", label: "URL", kind: "url" },
        ],
      },
      { key: "instagramUrl", label: "Instagram URL", kind: "url" },
      { key: "facebookUrl", label: "Facebook URL", kind: "url" },
      { key: "copyrightText", label: "Copyright line", kind: "text" },
    ],
    defaultFields: {
      brandBlurb:
        "Your premier concierge for golfing experiences across the world's finest courses. Book exclusive tee times in seconds.",
      supportLinks: [
        { label: "Help Center", href: "/help-center" },
        { label: "Contact Us", href: "/ContactUs" },
        { label: "FAQs", href: "/faqs" },
        { label: "Cancellation Policy", href: "/cancellation-policy" },
      ],
      companyLinks: [
        { label: "About Us", href: "/AboutUs" },
        { label: "Careers", href: "/careers" },
        { label: "Terms & Conditions", href: "/terms-of-service" },
        { label: "Privacy Policy", href: "/privacy-policy" },
      ],
      instagramUrl: "https://instagram.com/exqura",
      facebookUrl: "https://facebook.com/exqura",
      copyrightText: "© 2026 Exqura Golf & Experiences. All rights reserved.",
    },
  },

  "navbar-content": {
    label: "Navigation",
    page: "navbar",
    Renderer: ExquraNavbar,
    fieldsSchema: [
      { key: "logoImage", label: "Logo", kind: "image", helpText: "Leave empty to use the default Exqura wordmark." },
      { key: "logoAlt", label: "Logo alt text", kind: "text" },
      {
        key: "navItems",
        label: "Navigation items",
        kind: "list",
        itemLabel: "Item",
        itemFields: [
          {
            key: "type",
            label: "Type",
            kind: "select",
            options: ["link", "dropdown"],
          },
          { key: "label", label: "Label", kind: "text" },
          {
            key: "href",
            label: "Link URL",
            kind: "url",
            helpText: "Only used when Type is \"link\".",
          },
          {
            key: "items",
            label: "Dropdown items",
            kind: "list",
            itemLabel: "Menu item",
            itemFields: [
              { key: "label", label: "Label", kind: "text" },
              { key: "href", label: "URL", kind: "url" },
            ],
            helpText: "Only used when Type is \"dropdown\".",
          },
        ],
        helpText:
          "One ordered list — move-up/move-down reorders \"Book Golf\" relative to the plain links, same as any other list here.",
      },
      { key: "signInLabel", label: "\"Sign In\" button label", kind: "text" },
      {
        key: "homeLabel",
        label: "\"Home\" button label (logged-in)",
        kind: "text",
      },
      {
        key: "bookingsLabel",
        label: "\"Bookings\" button label (logged-in)",
        kind: "text",
      },
      {
        key: "profileLabel",
        label: "\"Profile\" button label (logged-in)",
        kind: "text",
        helpText:
          "The Logout button stays icon-only — no label to edit there.",
      },
    ],
    defaultFields: {
      logoImage: "",
      logoAlt: "Exqura",
      navItems: [
        {
          type: "dropdown",
          label: "Book Golf",
          href: "",
          items: [
            { label: "About Courses", href: "/about-courses" },
            { label: "Golf Getaways", href: "/golf-getaways" },
            { label: "Exqura Experience", href: "/exqura-experience" },
          ],
        },
        { type: "link", label: "About Us", href: "/AboutUs", items: [] },
        { type: "link", label: "Contact Us", href: "/ContactUs", items: [] },
      ],
      signInLabel: "Sign In",
      homeLabel: "Home",
      bookingsLabel: "Bookings",
      profileLabel: "Profile",
    },
  },
  "checkout-footnotes": {
    label: "Footnotes",
    page: "checkout",
    Renderer: CheckoutFootnotes,
    fieldsSchema: [
      {
        key: "text",
        label: "Footnote text",
        kind: "textarea",
        helpText:
          "Shown under the Golf Rounds pricing on the Review & Pay page, e.g. to note that caddie tips aren't included.",
      },
    ],
    defaultFields: {
      text:
        "Caddie tips are not included in the golf fees above and are usually paid directly in cash to the caddies.",
    },
  },

  // --- Generic blocks: not tied to any fixed page (`page: null`), only
  // ever offered on admin-created custom pages. Cover what a "text only"
  // page (an FAQ, a policy page) needs that no fixed page's dedicated
  // components already provide.
  "generic-rich-text": {
    label: "Text Section",
    page: null,
    reusable: true,
    Renderer: GenericRichText,
    fieldsSchema: [
      { key: "eyebrow", label: "Eyebrow text", kind: "text" },
      { key: "headline", label: "Headline", kind: "text" },
      {
        key: "body",
        label: "Body text",
        kind: "textarea",
        helpText: "Leave a blank line between paragraphs.",
      },
    ],
    defaultFields: {
      eyebrow: "",
      headline: "Section headline",
      body: "Write your content here.",
    },
  },

  "generic-link-cards": {
    label: "Link Cards",
    page: null,
    reusable: true,
    Renderer: GenericLinkCards,
    fieldsSchema: [
      { key: "eyebrow", label: "Eyebrow text", kind: "text" },
      { key: "headline", label: "Headline", kind: "text" },
      {
        key: "items",
        label: "Cards",
        kind: "list",
        itemLabel: "Card",
        itemFields: [
          { key: "icon", label: "Icon", kind: "icon" },
          { key: "title", label: "Title", kind: "text" },
          { key: "description", label: "Description", kind: "textarea" },
          { key: "linkLabel", label: "Link label", kind: "text" },
          { key: "href", label: "Link URL", kind: "url" },
        ],
      },
    ],
    defaultFields: {
      eyebrow: "",
      headline: "Section headline",
      items: [
        { icon: "Star", title: "Card title", description: "Card description.", linkLabel: "Learn more", href: "/" },
      ],
    },
  },

  "policy-document": {
    label: "Policy Document",
    page: null,
    reusable: true,
    Renderer: PolicyDocument,
    fieldsSchema: [
      { key: "icon", label: "Icon", kind: "icon" },
      { key: "eyebrow", label: "Eyebrow text", kind: "text" },
      { key: "title", label: "Title", kind: "text" },
      { key: "dateline", label: "Date line", kind: "text" },
      { key: "intro", label: "Introduction", kind: "textarea" },
      {
        key: "sections",
        label: "Sections",
        kind: "list",
        itemLabel: "Section",
        itemFields: [
          { key: "title", label: "Section title", kind: "text" },
          {
            key: "body",
            label: "Section text",
            kind: "textarea",
            helpText: "Each new line becomes its own paragraph.",
          },
        ],
      },
    ],
    defaultFields: {
      icon: "FileText",
      eyebrow: "Legal",
      title: "Policy title",
      dateline: "",
      intro: "",
      sections: [{ title: "1. Section title", body: "Write this section here." }],
    },
  },

  "generic-faq-list": {
    label: "FAQ List",
    page: null,
    reusable: true,
    Renderer: GenericFaqList,
    fieldsSchema: [
      { key: "eyebrow", label: "Eyebrow text", kind: "text" },
      { key: "headline", label: "Headline", kind: "text" },
      {
        key: "items",
        label: "Questions",
        kind: "list",
        itemLabel: "Question",
        itemFields: [
          { key: "question", label: "Question", kind: "text" },
          { key: "answer", label: "Answer", kind: "textarea" },
        ],
      },
    ],
    defaultFields: {
      eyebrow: "FAQ",
      headline: "Frequently Asked Questions",
      items: [
        {
          question: "How do I book a tee time?",
          answer:
            "Pick a golf getaway, choose your date and course, and complete checkout — you'll get an instant confirmation.",
        },
      ],
    },
  },
};

// About Courses gets its own banner doc (separate from the Getaways &
// Experience one) — same carousel block, just tied to a different page.
BLOCK_REGISTRY["about-courses-carousel"] = {
  ...BLOCK_REGISTRY["masthead-carousel"],
  page: "about-courses-masthead",
};

/** Block types available for a given page key, in a stable/registration
 * order. Fixed pages (in PAGES) only ever see blocks explicitly tied to
 * them via `page`. Any other pageKey is an admin-created custom page —
 * those get every block marked `reusable: true`, regardless of `page`. */
export function blockTypesForPage(pageKey) {
  const allowsReusable =
    !BUILTIN_PAGE_KEYS.has(pageKey) || PAGES[pageKey].reusableBlocks;
  return Object.entries(BLOCK_REGISTRY)
    .filter(([, def]) => def.page === pageKey || (allowsReusable && def.reusable))
    .map(([type, def]) => ({ type, ...def }));
}

export function getBlockDef(type) {
  return BLOCK_REGISTRY[type] || null;
}

/** Runs each block's optional `migrateFields` (see e.g. "home-hero" above)
 * over a blocks array loaded from Firestore/defaultContent, upgrading any
 * older-shaped `fields` to what the current fieldsSchema expects. Called
 * wherever a page's blocks are loaded for admin editing, so a schema
 * change never leaves existing content invisible/uneditable in "Edit
 * Site" — the next Save Draft/Publish persists the upgraded shape. */
export function migrateBlocks(blocks = []) {
  return blocks.map((block) => {
    const def = getBlockDef(block.type);
    if (!def?.migrateFields) return block;
    return { ...block, fields: def.migrateFields(block.fields) };
  });
}
